import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, AlertTriangle, ArrowDown, ArrowUp, ArrowRightLeft, Settings2, Boxes, Search, TrendingUp, TrendingDown, Package, FileSpreadsheet, FileDown, ChevronDown, ChevronRight, History } from 'lucide-react';
import {
  apiListStock, apiStockAlerts, apiListMovements, apiCreateMovement,
  apiListProducts, apiListWarehouses, apiListLocations, apiListPartners,
  apiStockByProduct, apiExportStockByProduct
} from '../../erpApi';
import { toast } from 'sonner';

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '';
const MOV_LABEL = { in: 'Carico', out: 'Scarico', transfer: 'Trasferimento', adjustment: 'Rettifica', production_in: 'Prod. Carico', production_out: 'Prod. Prelievo' };
const MOV_COLOR = {
  in: 'bg-emerald-100 text-emerald-700', adjustment: 'bg-amber-100 text-amber-700',
  out: 'bg-red-100 text-red-700', transfer: 'bg-blue-100 text-blue-700',
  production_in: 'bg-emerald-100 text-emerald-700', production_out: 'bg-violet-100 text-violet-700'
};

const MovementForm = ({ products, warehouses, onSubmit, onCancel }) => {
  const [f, setF] = useState({
    product_id: '', warehouse_id: '', movement_type: 'in',
    quantity: 0, lot_number: '', unit_cost: '',
    target_warehouse_id: '', reference: '', notes: ''
  });
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    if (f.warehouse_id) apiListLocations(f.warehouse_id).then(setLocations);
    else setLocations([]);
  }, [f.warehouse_id]);
  // Auto-fill lot from selected product
  useEffect(() => {
    if (f.product_id && !f.lot_number) {
      const p = products.find(x => x.id === f.product_id);
      if (p?.lot_number) setF(prev => ({ ...prev, lot_number: p.lot_number }));
    }
  }, [f.product_id]); // eslint-disable-line
  // Memoize destination warehouses for transfer (excludes current warehouse)
  const transferTargets = useMemo(
    () => warehouses.filter(w => w.id !== f.warehouse_id),
    [warehouses, f.warehouse_id]
  );
  const submit = (e) => {
    e.preventDefault();
    if (!f.product_id || !f.warehouse_id || !f.quantity) return toast.error('Compila tutti i campi obbligatori');
    if (f.movement_type === 'transfer' && !f.target_warehouse_id) return toast.error('Magazzino destinazione obbligatorio');
    const body = { ...f, quantity: parseFloat(f.quantity) };
    if (f.unit_cost) body.unit_cost = parseFloat(f.unit_cost);
    else delete body.unit_cost;
    if (!f.target_warehouse_id) delete body.target_warehouse_id;
    onSubmit(body);
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="movement-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Tipo movimento *</Label>
          <Select value={f.movement_type} onValueChange={(v) => setF({ ...f, movement_type: v })}>
            <SelectTrigger data-testid="mov-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="in">Carico (in)</SelectItem>
              <SelectItem value="out">Scarico (out)</SelectItem>
              <SelectItem value="transfer">Trasferimento</SelectItem>
              <SelectItem value="adjustment">Rettifica inventario</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Prodotto *</Label>
          <Select value={f.product_id} onValueChange={(v) => setF({ ...f, product_id: v })}>
            <SelectTrigger data-testid="mov-product"><SelectValue placeholder="Seleziona prodotto..." /></SelectTrigger>
            <SelectContent>
              {products.map(p => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Magazzino *</Label>
          <Select value={f.warehouse_id} onValueChange={(v) => setF({ ...f, warehouse_id: v })}>
            <SelectTrigger><SelectValue placeholder="Seleziona magazzino..." /></SelectTrigger>
            <SelectContent>
              {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Ubicazione</Label>
          <Select value={f.location_id || ''} onValueChange={(v) => setF({ ...f, location_id: v || null })} disabled={locations.length === 0}>
            <SelectTrigger><SelectValue placeholder={locations.length === 0 ? '—' : 'Opzionale'} /></SelectTrigger>
            <SelectContent>
              {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.code}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {f.movement_type === 'transfer' && (
        <div className="space-y-1.5"><Label>Magazzino destinazione *</Label>
          <Select value={f.target_warehouse_id} onValueChange={(v) => setF({ ...f, target_warehouse_id: v })}>
            <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
            <SelectContent>
              {transferTargets.map(w => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label>Quantità *</Label><Input type="number" step="0.001" min="0" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} required /></div>
        <div className="space-y-1.5"><Label>Lotto</Label><Input value={f.lot_number} onChange={(e) => setF({ ...f, lot_number: e.target.value })} placeholder="Opzionale" /></div>
        <div className="space-y-1.5"><Label>Costo unit. (solo carico)</Label><Input type="number" step="0.001" value={f.unit_cost} onChange={(e) => setF({ ...f, unit_cost: e.target.value })} placeholder="€/u" disabled={!['in', 'adjustment'].includes(f.movement_type)} /></div>
      </div>
      <div className="space-y-1.5"><Label>Riferimento / Note</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="es. DDT-2026-001, Inventario annuale" /></div>
      <DialogFooter><Button type="button" variant="outline" onClick={onCancel}>Annulla</Button><Button data-testid="mov-submit" type="submit" className="bg-blue-700 hover:bg-blue-800">Registra movimento</Button></DialogFooter>
    </form>
  );
};

const StockManager = () => {
  const [stock, setStock] = useState([]);
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [bySku, setBySku] = useState({ rows: [], summary: null });
  const [expandedSku, setExpandedSku] = useState({}); // { product_id: { loading, movs } }
  const toggleSkuHistory = async (productId) => {
    const cur = expandedSku[productId];
    if (cur && cur.movs) {
      // toggle close
      setExpandedSku(prev => ({ ...prev, [productId]: { ...cur, open: !cur.open } }));
      return;
    }
    setExpandedSku(prev => ({ ...prev, [productId]: { loading: true, open: true } }));
    try {
      const movs = await apiListMovements({ product_id: productId });
      setExpandedSku(prev => ({ ...prev, [productId]: { loading: false, open: true, movs } }));
    } catch {
      toast.error('Errore caricamento storico');
      setExpandedSku(prev => ({ ...prev, [productId]: { loading: false, open: false, movs: [] } }));
    }
  };
  const [whFilter, setWhFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [lowOnly, setLowOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [lotFilter, setLotFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const reload = async () => {
    const params = {};
    if (whFilter !== 'all') params.warehouse_id = whFilter;
    if (customerFilter !== 'all') params.customer_id = customerFilter;
    if (lowOnly) params.low_stock_only = true;
    if (search) params.search = search;
    if (lotFilter) params.lot_number = lotFilter;
    // Aggregated params (no lot, no low_stock filter — disponibilità globale per SKU)
    const aggParams = {};
    if (whFilter !== 'all') aggParams.warehouse_id = whFilter;
    if (customerFilter !== 'all') aggParams.customer_id = customerFilter;
    if (search) aggParams.search = search;
    const [s, m, p, w, c, agg] = await Promise.all([
      apiListStock(params),
      apiListMovements(whFilter !== 'all' ? { warehouse_id: whFilter } : {}),
      apiListProducts(),
      apiListWarehouses(),
      apiListPartners({ kind: 'customer' }),
      apiStockByProduct(aggParams),
    ]);
    setStock(s); setMovements(m); setProducts(p); setWarehouses(w); setCustomers(c); setBySku(agg);
  };
  useEffect(() => { reload(); }, [whFilter, customerFilter, lowOnly, search, lotFilter]); // eslint-disable-line
  const handleSubmit = async (data) => {
    try {
      await apiCreateMovement(data);
      toast.success('Movimento registrato');
      setDialogOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const totalValuation = stock.reduce((a, b) => a + (b.valuation || 0), 0);
  return (
    <div className="space-y-3" data-testid="stock-manager">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={whFilter} onValueChange={setWhFilter}>
            <SelectTrigger data-testid="stock-wh-filter" className="h-9 w-[180px] text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i magazzini</SelectItem>
              {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger data-testid="stock-customer-filter" className="h-9 w-[180px] text-sm"><SelectValue placeholder="Cliente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i clienti</SelectItem>
              {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.business_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="stock-search" placeholder="Codice / descrizione..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-8 w-[200px] text-sm" />
          </div>
          <Input data-testid="stock-lot-filter" placeholder="Lotto..." value={lotFilter} onChange={(e) => setLotFilter(e.target.value)} className="h-9 w-[140px] text-sm font-mono" />
          <button onClick={() => setLowOnly(v => !v)} data-testid="stock-low-toggle"
            className={`h-9 px-3 rounded-md text-xs font-medium flex items-center gap-1.5 border ${lowOnly ? 'bg-red-50 border-red-200 text-red-700' : 'bg-white border-slate-200 text-slate-600'}`}>
            <AlertTriangle className="w-3.5 h-3.5" />Solo sotto-scorta
          </button>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="mov-new" className="bg-blue-700 hover:bg-blue-800 gap-2 h-9"><Plus className="w-4 h-4" />Nuovo movimento</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader><DialogTitle>Nuovo movimento magazzino</DialogTitle><DialogDescription>Carico, scarico, trasferimento o rettifica inventario.</DialogDescription></DialogHeader>
            <MovementForm products={products} warehouses={warehouses} onSubmit={handleSubmit} onCancel={() => setDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between">
        <div className="text-sm">
          <span className="text-slate-500">Articoli a giacenza: </span><span className="font-semibold">{stock.length}</span>
        </div>
        <div className="text-sm">
          <span className="text-slate-500">Valorizzazione: </span><span className="font-semibold text-blue-700 text-base">€{totalValuation.toFixed(2)}</span>
        </div>
      </div>

      <Tabs defaultValue="by-sku">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="by-sku" data-testid="stock-tab-by-sku">Disponibile per SKU</TabsTrigger>
          <TabsTrigger value="stock" data-testid="stock-tab-stock">Per magazzino/lotto</TabsTrigger>
          <TabsTrigger value="movements" data-testid="stock-tab-movs">Movimenti</TabsTrigger>
        </TabsList>

        <TabsContent value="by-sku" className="mt-3 space-y-3">
          {/* Export buttons */}
          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm" variant="outline" className="h-8 gap-1.5"
              data-testid="stock-export-xlsx"
              onClick={async () => {
                try {
                  const filters = {};
                  if (whFilter !== 'all') filters.warehouse_id = whFilter;
                  if (customerFilter !== 'all') filters.customer_id = customerFilter;
                  if (search) filters.search = search;
                  const fn = await apiExportStockByProduct('xlsx', filters);
                  toast.success(`Esportato ${fn}`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Errore esportazione Excel');
                }
              }}
            >
              <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-700" />Esporta Excel
            </Button>
            <Button
              size="sm" variant="outline" className="h-8 gap-1.5"
              data-testid="stock-export-pdf"
              onClick={async () => {
                try {
                  const filters = {};
                  if (whFilter !== 'all') filters.warehouse_id = whFilter;
                  if (customerFilter !== 'all') filters.customer_id = customerFilter;
                  if (search) filters.search = search;
                  const fn = await apiExportStockByProduct('pdf', filters);
                  toast.success(`Esportato ${fn}`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Errore esportazione PDF');
                }
              }}
            >
              <FileDown className="w-3.5 h-3.5 text-red-700" />Esporta PDF
            </Button>
          </div>
          {/* Summary KPI cards */}
          {bySku.summary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <div className="p-3 bg-white rounded-xl border border-slate-200">
                <div className="flex items-center gap-2 text-[10px] uppercase text-slate-500 font-medium"><Package className="w-3 h-3" />SKU totali</div>
                <div className="text-xl font-bold text-slate-900 tabular-nums mt-1">{bySku.summary.total_skus}</div>
              </div>
              <div className="p-3 bg-white rounded-xl border border-emerald-100">
                <div className="flex items-center gap-2 text-[10px] uppercase text-emerald-700 font-medium"><TrendingUp className="w-3 h-3" />Carichi totali</div>
                <div className="text-xl font-bold text-emerald-700 tabular-nums mt-1">+{bySku.summary.total_in.toLocaleString('it-IT')}</div>
              </div>
              <div className="p-3 bg-white rounded-xl border border-amber-100">
                <div className="flex items-center gap-2 text-[10px] uppercase text-amber-700 font-medium"><TrendingDown className="w-3 h-3" />Scarichi totali</div>
                <div className="text-xl font-bold text-amber-700 tabular-nums mt-1">−{bySku.summary.total_out.toLocaleString('it-IT')}</div>
              </div>
              <div className="p-3 bg-white rounded-xl border border-blue-200">
                <div className="flex items-center gap-2 text-[10px] uppercase text-blue-700 font-medium"><Boxes className="w-3 h-3" />Disponibile totale</div>
                <div className="text-xl font-bold text-blue-700 tabular-nums mt-1">{bySku.summary.total_available.toLocaleString('it-IT')}</div>
              </div>
              <div className={`p-3 bg-white rounded-xl border ${bySku.summary.negative_count > 0 ? 'border-red-200' : 'border-slate-200'}`}>
                <div className="flex items-center gap-2 text-[10px] uppercase font-medium" style={{ color: bySku.summary.negative_count > 0 ? '#b91c1c' : '#64748b' }}><AlertTriangle className="w-3 h-3" />Alert</div>
                <div className={`text-sm font-semibold mt-1 ${bySku.summary.negative_count > 0 ? 'text-red-700' : 'text-slate-700'}`}>
                  {bySku.summary.negative_count > 0 && <div>{bySku.summary.negative_count} negativi</div>}
                  {bySku.summary.below_min_count > 0 && <div className="text-amber-700">{bySku.summary.below_min_count} sotto-scorta</div>}
                  {bySku.summary.negative_count === 0 && bySku.summary.below_min_count === 0 && <div className="text-emerald-700">OK</div>}
                </div>
              </div>
            </div>
          )}

          {bySku.rows.length === 0 ? (
            <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300"><Boxes className="w-10 h-10 text-slate-300 mx-auto mb-2" /><div className="text-sm">Nessun prodotto</div></div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="by-sku-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">SKU</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Nome prodotto</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Cliente</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-emerald-700">↑ Carichi</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-amber-700">↓ Scarichi</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-blue-700">Disponibile</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Min</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">u.m.</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Valore €</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {bySku.rows.map(r => {
                    let availCls = 'text-blue-700';
                    if (r.is_negative) availCls = 'text-red-700';
                    else if (r.below_min) availCls = 'text-amber-700';
                    const ex = expandedSku[r.product_id];
                    const isOpen = !!(ex && ex.open);
                    return (
                      <React.Fragment key={r.product_id}>
                        <tr className={`hover:bg-slate-50 ${r.is_negative ? 'bg-red-50/50' : ''}`} data-testid={`by-sku-row-${r.sku}`}>
                          <td className="px-2 py-2">
                            <button
                              type="button" onClick={() => toggleSkuHistory(r.product_id)}
                              data-testid={`by-sku-expand-${r.sku}`}
                              className="p-1 hover:bg-slate-200 rounded transition-colors" title="Storico movimenti">
                              {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
                            </button>
                          </td>
                          <td className="px-3 py-2 font-mono font-semibold">{r.sku}</td>
                          <td className="px-3 py-2 text-slate-800">{r.name}</td>
                          <td className="px-3 py-2 text-xs text-slate-500">{r.customer_name || '—'}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{r.total_in > 0 ? `+${r.total_in}` : '—'}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-amber-700">{r.total_out > 0 ? `−${r.total_out}` : '—'}</td>
                          <td className={`px-3 py-2 text-right tabular-nums font-bold text-base ${availCls}`} data-testid={`by-sku-avail-${r.sku}`}>{r.available}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-xs text-slate-500">{r.min_stock || '—'}</td>
                          <td className="px-3 py-2 text-right text-xs text-slate-500">{r.unit_of_measure}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-xs text-slate-600">€{(r.valuation || 0).toFixed(2)}</td>
                        </tr>
                        {isOpen && (
                          <tr data-testid={`by-sku-history-${r.sku}`}>
                            <td colSpan={10} className="bg-slate-50 border-t border-slate-200 p-0">
                              <div className="px-6 py-3">
                                <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-slate-700">
                                  <History className="w-3.5 h-3.5" />Storico movimenti — {r.sku}
                                </div>
                                {ex.loading ? (
                                  <div className="text-xs text-slate-500 py-3">Caricamento...</div>
                                ) : !ex.movs || ex.movs.length === 0 ? (
                                  <div className="text-xs text-slate-500 py-3 italic">Nessun movimento registrato</div>
                                ) : (
                                  <table className="w-full text-xs bg-white border border-slate-200 rounded">
                                    <thead className="bg-slate-100">
                                      <tr>
                                        <th className="text-left px-2 py-1.5">Tipo</th>
                                        <th className="text-left px-2 py-1.5">Data/Ora</th>
                                        <th className="text-right px-2 py-1.5">Quantità</th>
                                        <th className="text-left px-2 py-1.5">u.m.</th>
                                        <th className="text-left px-2 py-1.5">Magazzino</th>
                                        <th className="text-left px-2 py-1.5">Lotto</th>
                                        <th className="text-left px-2 py-1.5">Riferimento</th>
                                        <th className="text-left px-2 py-1.5">Note</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                      {ex.movs.map(m => {
                                        const isIn = ['in', 'production_in'].includes(m.movement_type);
                                        const isOut = ['out', 'production_out'].includes(m.movement_type);
                                        let tipoLabel = 'Rettifica';
                                        let tipoCls = 'bg-slate-200 text-slate-700';
                                        if (isIn) { tipoLabel = 'Carico'; tipoCls = 'bg-emerald-100 text-emerald-800'; }
                                        else if (isOut) { tipoLabel = 'Scarico'; tipoCls = 'bg-amber-100 text-amber-800'; }
                                        else if (m.movement_type === 'transfer') { tipoLabel = 'Trasferimento'; tipoCls = 'bg-blue-100 text-blue-800'; }
                                        const ts = new Date(m.timestamp).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                                        return (
                                          <tr key={m.id}>
                                            <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${tipoCls}`}>{tipoLabel}</span></td>
                                            <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{ts}</td>
                                            <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${isOut ? 'text-amber-700' : isIn ? 'text-emerald-700' : 'text-slate-700'}`}>
                                              {isOut ? '−' : '+'}{m.quantity}
                                            </td>
                                            <td className="px-2 py-1.5 text-slate-500">{r.unit_of_measure}</td>
                                            <td className="px-2 py-1.5 font-mono text-slate-600">{m.warehouse_code || '—'}</td>
                                            <td className="px-2 py-1.5 font-mono text-slate-600">{m.lot_number || '—'}</td>
                                            <td className="px-2 py-1.5 text-slate-700">{m.reference || '—'}</td>
                                            <td className="px-2 py-1.5 text-slate-500 truncate max-w-[280px]" title={m.notes}>{m.notes || '—'}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                  <tr>
                    <td colSpan={3} className="px-3 py-2 text-[11px] font-semibold uppercase text-slate-700">Totale generale</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-emerald-700">+{bySku.summary?.total_in ?? 0}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-amber-700">−{bySku.summary?.total_out ?? 0}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-blue-700 text-base" data-testid="by-sku-grand-total">{bySku.summary?.total_available ?? 0}</td>
                    <td colSpan={2}></td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-slate-800">€{(bySku.summary?.total_valuation ?? 0).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="stock" className="mt-3">
          {stock.length === 0 ? (
            <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300"><Boxes className="w-10 h-10 text-slate-300 mx-auto mb-2" /><div className="text-sm">Nessuna giacenza</div></div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">SKU</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Prodotto</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Cliente</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Magazzino</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Ubic.</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Lotto</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Q.tà residua</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Min</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Costo medio</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Valore</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {stock.map(s => (
                    <tr key={`${s.id}`} className={`hover:bg-slate-50 ${s.below_min ? 'bg-red-50/30' : ''}`}>
                      <td className="px-3 py-2 font-mono text-xs">{s.product_sku}</td>
                      <td className="px-3 py-2">{s.product_name}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{s.customer_name || '—'}</td>
                      <td className="px-3 py-2 text-xs"><span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded mr-1">{s.warehouse_code}</span></td>
                      <td className="px-3 py-2 text-xs font-mono">{s.location_code || '—'}</td>
                      <td className="px-3 py-2 text-xs font-mono">{s.lot_number || '—'}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold ${s.below_min ? 'text-red-700' : 'text-slate-900'}`}>
                        {s.quantity} {s.unit_of_measure}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-xs">{s.min_stock || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-xs">€{(s.avg_cost || 0).toFixed(3)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-blue-700">€{(s.valuation || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
        <TabsContent value="movements" className="mt-3">
          {movements.length === 0 ? (
            <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300"><Settings2 className="w-10 h-10 text-slate-300 mx-auto mb-2" /><div className="text-sm">Nessun movimento</div></div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Data</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Tipo</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Prodotto</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Magazzino</th>
                    <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Q.tà</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Lotto</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Riferimento</th>
                    <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Operatore</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {movements.map(m => (
                    <tr key={m.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-xs text-slate-600">{fmtDate(m.timestamp)}</td>
                      <td className="px-3 py-2"><span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium ${MOV_COLOR[m.movement_type]}`}>
                        {['in', 'production_in'].includes(m.movement_type) && <ArrowDown className="w-3 h-3" />}
                        {['out', 'production_out'].includes(m.movement_type) && <ArrowUp className="w-3 h-3" />}
                        {m.movement_type === 'transfer' && <ArrowRightLeft className="w-3 h-3" />}
                        {MOV_LABEL[m.movement_type]}
                      </span></td>
                      <td className="px-3 py-2"><span className="font-mono text-xs text-slate-500 mr-1">{m.product_sku}</span><span>{m.product_name}</span></td>
                      <td className="px-3 py-2 text-xs">
                        <span className="font-mono">{m.warehouse_code}</span>
                        {m.target_warehouse_code && <span className="text-slate-400"> → <span className="font-mono">{m.target_warehouse_code}</span></span>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">{m.quantity}</td>
                      <td className="px-3 py-2 text-xs">{m.lot_number || '—'}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{m.reference || '—'}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{m.user_email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StockManager;
